import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SegmentedControl from '@/components/ui/SegmentedControl';
import HeroImage from '@/components/ui/HeroImage';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppHeader from '@/components/ui/AppHeader';
import { loginSchema, type LoginFormData } from '@/utils/validators';
import { useAuthStore } from '@/store/authStore';
import authService from '@/services/authService';

const REASON_MESSAGE: Record<string, { title: string; body: string; tone: 'warning' | 'danger' | 'info' }> = {
  suspended: {
    title: 'Compte suspendu',
    body: 'Votre compte a été suspendu par votre gestionnaire. Vous ne pouvez pas vous connecter pour le moment. Contactez votre gestionnaire pour plus d\'informations.',
    tone: 'warning',
  },
  terminated: {
    title: 'Compte résilié',
    body: 'Votre couverture a été résiliée. L\'accès à l\'application n\'est plus possible. Contactez votre gestionnaire si vous pensez qu\'il s\'agit d\'une erreur.',
    tone: 'danger',
  },
  inactive: {
    title: 'Compte non actif',
    body: 'Votre compte n\'est pas actif. Contactez votre gestionnaire.',
    tone: 'info',
  },
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login: setAuthState } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>('password');
  const [showPassword, setShowPassword] = useState(false);

  const reasonNotice = useMemo(() => {
    const reason = searchParams.get('reason');
    return reason ? REASON_MESSAGE[reason] : undefined;
  }, [searchParams]);

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const response = await authService.login({ 
        email: data.email, 
        password: data.password,
        loginMethod: 'EMAIL_PASSWORD' as any
      });
      
      setAuthState(response.user, response.tokens.accessToken, response.tokens.refreshToken);
      toast.success('Connexion réussie!');
      navigate(from, { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSegmentChange = (value: string) => {
    const method = value as 'password' | 'otp';
    setLoginMethod(method);
    if (method === 'otp') {
      navigate('/login/otp');
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col mx-auto max-w-[480px] bg-white dark:bg-background-dark">
      <AppHeader title="Connexion" />

      {/* Scrollable Content */}
      <div className="flex-1 flex flex-col">
        {/* Hero Image */}
        <HeroImage
          imageUrl="/images/login-hero.png"
          alt="Médecin en consultation avec une patiente dans un cabinet moderne"
          badge="ASSURANCE SANTÉ"
        />

        {/* Headline Text */}
        <div className="px-6 flex flex-col items-center">
          <h1 className="text-slate-900 dark:text-white tracking-tight text-2xl md:text-3xl font-bold leading-tight text-center mb-2">
            Bienvenue sur votre{' '}
            <span className="text-emerald-600 dark:text-primary relative inline-block">
              espace santé
              <svg 
                className="absolute w-full h-2 bottom-0 left-0 text-primary/30 -z-10" 
                preserveAspectRatio="none" 
                viewBox="0 0 100 10"
              >
                <path d="M0 5 Q 50 10 100 5" fill="none" stroke="currentColor" strokeWidth="8" />
              </svg>
            </span>
          </h1>
          <p className="text-slate-500 dark:text-gray-400 text-base font-normal leading-relaxed text-center max-w-xs">
            Connectez-vous pour accéder à vos droits, remboursements et carte tiers payant.
          </p>
        </div>

        {reasonNotice && (
          <div
            role="alert"
            className={`mx-6 mt-4 rounded-xl border p-4 ${
              reasonNotice.tone === 'danger'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-900 dark:text-red-100'
                : reasonNotice.tone === 'warning'
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50 text-orange-900 dark:text-orange-100'
                  : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
            }`}
          >
            <p className="font-bold text-sm leading-tight">{reasonNotice.title}</p>
            <p className="text-xs mt-1 leading-snug opacity-90">{reasonNotice.body}</p>
          </div>
        )}

        {/* Segmented Control */}
        <div className="px-6 py-6">
          <SegmentedControl
            options={[
              { id: 'password', label: 'Mot de passe' },
              { id: 'otp', label: 'Code Email (OTP)' },
            ]}
            value={loginMethod}
            onChange={handleSegmentChange}
          />
          {loginMethod === 'otp' && (
            <p className="mt-2 text-xs text-gray-500 text-center animate-in fade-in slide-in-from-top-1">
              Un code de validation unique vous sera envoyé sur votre adresse email enregistrée.
            </p>
          )}
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 flex flex-col gap-5 pb-8">
          {/* Email Input */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="login-email" className="text-sm font-medium text-slate-700 dark:text-gray-300 ml-1">
              Email
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <MaterialIcon
                  icon="person"
                  className="text-gray-400 group-focus-within:text-primary transition-colors"
                />
              </div>
              <input
                {...register('email')}
                id="login-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 dark:bg-white/5 border-transparent focus:border-primary focus:ring-0 rounded-xl text-base text-slate-900 dark:text-white placeholder:text-gray-400 transition-all shadow-sm focus:bg-white dark:focus:bg-black/20"
                placeholder="exemple@email.com"
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-600 ml-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password Input */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="login-password" className="text-sm font-medium text-slate-700 dark:text-gray-300 ml-1">
              Mot de passe
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <MaterialIcon 
                  icon="lock" 
                  className="text-gray-400 group-focus-within:text-primary transition-colors"
                />
              </div>
              <input
                {...register('password')}
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="block w-full pl-11 pr-12 py-3.5 bg-gray-50 dark:bg-white/5 border-transparent focus:border-primary focus:ring-0 rounded-xl text-base text-slate-900 dark:text-white placeholder:text-gray-400 transition-all shadow-sm focus:bg-white dark:focus:bg-black/20"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-emerald-600 dark:hover:text-primary cursor-pointer transition-colors"
              >
                <MaterialIcon 
                  icon={showPassword ? 'visibility' : 'visibility_off'} 
                  style={{ fontSize: 20 }} 
                />
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-600 ml-1">{errors.password.message}</p>
            )}
          </div>

          {/* Forgot Password */}
          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-emerald-600 dark:text-primary hover:underline decoration-2 underline-offset-4"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="mt-4 w-full bg-primary hover:bg-[#25d360] active:scale-[0.98] transition-all duration-200 text-[#102216] text-base font-bold h-14 rounded-xl shadow-[0_4px_14px_rgba(43,238,108,0.4)] hover:shadow-[0_6px_20px_rgba(43,238,108,0.5)] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{isLoading ? 'Connexion...' : 'Se connecter'}</span>
            <MaterialIcon icon="arrow_forward" style={{ fontSize: 20 }} className="font-bold" />
          </button>

          {/* Secure Badge */}
          <div className="flex items-center justify-center gap-1.5 mt-2 opacity-70">
            <MaterialIcon 
              icon="verified_user" 
              className="text-emerald-600 dark:text-primary"
              style={{ fontSize: 16 }}
            />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Connexion sécurisée
            </span>
          </div>

          {/* Activation Link - Moved from Footer */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/5 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Compte créé par votre assureur ?{' '}
              <Link
                to="/forgot-password"
                className="font-bold text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-primary transition-colors ml-1"
              >
                Activez votre espace
              </Link>
            </p>
          </div>
        </form>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Footer */}
        <div className="p-6 pb-8 border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm rounded-t-3xl mt-2">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/support"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-medium text-gray-600 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-white/20 transition-all"
              >
                <MaterialIcon icon="headset_mic" style={{ fontSize: 18 }} />
                Besoin d'aide ?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
